import { Expose, Type, Exclude } from 'class-transformer';
import { Feature, MultiPolygon, Point } from 'geojson';

import { Default, ExposeRawData, Plunk } from '@lib/common/decorators';
import { colorIsDark } from '@lib/common/helpers';
import { SnCollection, SnModel } from '@lib/common/models';
import { ISnIsSharable } from '@lib/share';
import { COLORS } from '@lib/ui/theme';

import { SnContentType, IHasContentType } from '../base';
import { FususRegionCode } from '../constants';
import { SnOrganization } from '../organization';
import { SnShareInfo, SnSharePermissions } from '../share';
import { SnCameraStats } from '../stats';
import { SnTag } from '../tag';
import { SnFloor } from './floor';
import { SnLocationType } from './location-type';

export class SnLocation extends SnModel implements ISnIsSharable, IHasContentType {
  @Expose()
  public id: string;

  @Expose()
  public name: string;

  @Expose()
  public address: string;

  @Expose()
  public city: string;

  @Expose()
  public state: string;

  @Expose()
  public zip: string;

  @Expose()
  public contactName: string;

  @Expose()
  public contactEmail: string;

  @Expose()
  public contactPhone: string;

  @Expose()
  public internalCamera: number;

  @Expose()
  public externalCamera: number;

  @Expose()
  public defaultZoom: number;

  @Expose()
  public defaultFloor: string;

  @Expose()
  public media: any;

  @Expose()
  public geofence: MultiPolygon;

  @Expose()
  public distance: number;

  @Expose()
  @ExposeRawData()
  public additionalData: any;

  @Expose()
  @Type(() => SnLocationType)
  @Plunk('id', { toPlainOnly: true, groups: ['form'] })
  public type: SnLocationType;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnOrganization)
  public organization: SnOrganization;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public isShared: boolean;

  @Expose()
  public region?: FususRegionCode;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnShareInfo)
  public shareInfo: SnShareInfo;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnCameraStats)
  public readonly stats: SnCameraStats;

  @Expose()
  @ExposeRawData()
  public geometry: Point;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnTag)
  @Default(new SnCollection())
  public tags: SnCollection<SnTag>;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnFloor)
  @Default(new SnCollection())
  public readonly floors: SnCollection<SnFloor>;

  public readonly contentType = SnContentType.Location;

  @Expose()
  public createdTimestamp: Date;

  constructor(data: any = {}) {
    super();

    this.fromJSON(data);
  }

  get hasContact(): boolean {
    return !!this.contactName || !!this.contactEmail || !!this.contactPhone;
  }

  get fullAddress(): string {
    return `${this.address}, ${this.city}, ${this.state} ${this.zip}`;
  }

  get coords(): { lng: number; lat: number } {
    return {
      lng: this.geometry.coordinates[0],
      lat: this.geometry.coordinates[1],
    };
  }

  get canEdit(): boolean {
    return !this.isShared || this.shareInfo.permissions === SnSharePermissions.Edit;
  }

  get isOwned(): boolean {
    return !this.isShared || this.shareInfo.direction === 'Out';
  }

  public addressToHtml(): string {
    return `${this.address}<br />${this.city}, ${this.state} ${this.zip}`;
  }

  public toGeoJSON(): Feature<Point> {
    return {
      type: 'Feature',
      geometry: this.geometry,
      id: this.id,
      properties: {
        id: this.id,
        name: this.name,
        background: this.type.color,
        icon: String.fromCharCode(Number(`0x${this.type.icon}`)),
        iconColor: colorIsDark(this.type.color) ? COLORS['grey-0'] : '#000000',
      },
    };
  }
}
